package es.cragma.fichajefacil.ui.screens.auth

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import es.cragma.fichajefacil.data.AppRepository
import es.cragma.fichajefacil.model.LoginAccessMode
import es.cragma.fichajefacil.model.User
import es.cragma.fichajefacil.ui.components.RoleBadge
import es.cragma.fichajefacil.ui.components.StyledButtonPrimary
import es.cragma.fichajefacil.ui.components.StyledButtonWhite
import es.cragma.fichajefacil.ui.i18n.AppLanguage
import es.cragma.fichajefacil.ui.i18n.LocalizedStrings
import es.cragma.fichajefacil.ui.theme.*

@Composable
fun ModeSelectionDialog(
    user: User,
    availableAccounts: List<User> = emptyList(),
    repository: AppRepository? = null,
    currentLanguage: AppLanguage = AppLanguage.SPANISH,
    onUserSelected: ((User) -> Unit)? = null,
    onModeSelected: (LoginAccessMode) -> Unit,
    onLogout: () -> Unit = {}
) {
    val s = LocalizedStrings.get(currentLanguage)
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.6f))
            .padding(24.dp),
        contentAlignment = Alignment.Center
    ) {
        Card(
            modifier = Modifier
                .fillMaxWidth()
                .widthIn(max = 520.dp),
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(28.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = s.welcomeUser.replace("{name}", user.name),
                    fontSize = 22.sp,
                    fontWeight = FontWeight.Bold,
                    color = CragmaNavyDark
                )

                Spacer(modifier = Modifier.height(8.dp))

                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = "Rol:",
                        fontSize = 13.sp,
                        color = CragmaTextSecondary,
                        modifier = Modifier.padding(end = 6.dp)
                    )
                    RoleBadge(role = user.role, currentLanguage = currentLanguage)
                }

                // If user is registered in multiple companies
                if (availableAccounts.size > 1) {
                    Spacer(modifier = Modifier.height(20.dp))
                    Text(
                        text = "🏢 Selecciona la Empresa:",
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Bold,
                        color = CragmaNavyDark
                    )
                    Spacer(modifier = Modifier.height(8.dp))

                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        availableAccounts.forEach { acc ->
                            val isSelected = acc.id == user.id
                            val companyName = acc.companyId?.let { repository?.getCompany(it)?.name } ?: "Empresa (${acc.companyId ?: "Global"})"

                            Card(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable {
                                        if (!isSelected && onUserSelected != null) {
                                            onUserSelected(acc)
                                        }
                                    },
                                shape = RoundedCornerShape(12.dp),
                                border = if (isSelected) BorderStroke(2.dp, MaterialTheme.colorScheme.primary) else null,
                                colors = CardDefaults.cardColors(
                                    containerColor = if (isSelected) MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f) else Color(0xFFF1F5F9)
                                )
                            ) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(12.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Column(modifier = Modifier.weight(1f)) {
                                        Text(
                                            text = companyName,
                                            fontSize = 14.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = CragmaNavyDark
                                        )
                                        Text(
                                            text = acc.position.ifBlank { "Empleado" },
                                            fontSize = 12.sp,
                                            color = CragmaTextSecondary
                                        )
                                    }
                                    RoleBadge(role = acc.role, currentLanguage = currentLanguage)
                                }
                            }
                        }
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))

                Text(
                    text = s.selectModeSubtitle.replace("{role}", user.role.displayName),
                    fontSize = 13.sp,
                    color = CragmaTextSecondary,
                    textAlign = TextAlign.Center
                )

                Spacer(modifier = Modifier.height(24.dp))

                // Option 1: Worker Mode (Fichar)
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onModeSelected(LoginAccessMode.WORKER) },
                    shape = RoundedCornerShape(16.dp),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFF8FAFC)),
                    elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
                ) {
                    Column(
                        modifier = Modifier.padding(16.dp)
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(text = "⏱", fontSize = 24.sp)
                            Spacer(modifier = Modifier.width(10.dp))
                            Column {
                                Text(
                                    text = s.workerModeTitle,
                                    fontSize = 15.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = CragmaNavyDark
                                )
                                Text(
                                    text = s.workerModeDesc,
                                    fontSize = 12.sp,
                                    color = CragmaTextSecondary
                                )
                            }
                        }
                        Spacer(modifier = Modifier.height(12.dp))
                        StyledButtonWhite(
                            text = s.enterWorkerMode,
                            onClick = { onModeSelected(LoginAccessMode.WORKER) }
                        )
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))

                // Option 2: Management Mode (Administración) - only shown if role has management rights
                if (user.role.isManagement) {
                    Spacer(modifier = Modifier.height(16.dp))
                    Card(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable { onModeSelected(LoginAccessMode.MANAGEMENT) },
                        shape = RoundedCornerShape(16.dp),
                        colors = CardDefaults.cardColors(containerColor = CragmaNavyDark)
                    ) {
                        Column(
                            modifier = Modifier.padding(16.dp)
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(text = "📊", fontSize = 24.sp)
                                Spacer(modifier = Modifier.width(10.dp))
                                Column {
                                    Text(
                                        text = s.managementModeTitle,
                                        fontSize = 15.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = Color.White
                                    )
                                    Text(
                                        text = s.managementModeDesc,
                                        fontSize = 12.sp,
                                        color = Color(0xFF94A3B8)
                                    )
                                }
                            }
                            Spacer(modifier = Modifier.height(12.dp))
                            StyledButtonPrimary(
                                text = s.enterManagementMode,
                                onClick = { onModeSelected(LoginAccessMode.MANAGEMENT) }
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))

                TextButton(
                    onClick = onLogout,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = "🚪 Cerrar sesión",
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                        color = CragmaRedDanger
                    )
                }
            }
        }
    }
}
